import { BenefitsActivatedCard } from '@components/Common/BenefitsActivatedCard';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { CampaignGetFromCodeRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, useAppForm } from 'noddi-ui-web';
import { useAuthContext, useShouldFireRequestForLoggedInUser } from 'noddi-web';
import { z } from 'zod';
import { showNewUserPromotionIneligibleToast } from '../../utils/promotionActivation';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { CampaignClaimBottomBar } from './CampaignClaimBottomBar';

const AUTH_CODE_FORM_ID = 'activate-campaign-auth-code-form';

type Props = {
  campaign: CampaignGetFromCodeRecord;
  canNotActivateCoupons?: boolean;
};

export const ActivateCampaignFromCode = ({ campaign, canNotActivateCoupons }: Props) => {
  const requiresAuthCode = campaign.requiresAuthCode;
  const { currentUserGroupId, userData } = useAuthContext();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();

  const {
    data: claimStatus,
    isPending: isClaimStatusPending,
    error: claimStatusError
  } = noddiAsync.useGet({
    type: URLKeys.getCampaignClaimStatusFromCode,
    input: { code: campaign.code, userGroupId: currentUserGroupId! },
    queryConfig: {
      enabled: shouldFireRequest
    }
  });

  const {
    mutate: activateCampaign,
    isPending: isActivateCampaignPending,
    isSuccess: isActivateCampaignSuccess,
    error: activateCampaignError
  } = noddiAsync.usePost({
    type: URLKeys.postActivateCampaign,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getCampaignClaimStatusFromCode,
          input: { code: campaign.code, userGroupId: currentUserGroupId! }
        });
      }
    }
  });

  const form = useAppForm({
    defaultValues: { authCode: '' },
    validationLogic: revalidateLogic(),
    validators: {
      onSubmit: z.object({
        authCode: requiresAuthCode ? z.string().min(1, t`Required`) : z.string()
      })
    },
    onSubmit: ({ value }) => {
      if (canNotActivateCoupons) {
        showNewUserPromotionIneligibleToast(`${userData?.user.firstName} ${userData?.user.lastName}`);
        return;
      }
      activateCampaign({
        userGroupId: currentUserGroupId!,
        campaignId: campaign.id,
        brandDomain: campaign.brandDomain,
        authCode: value.authCode
      });
    }
  });

  const userName = `${userData?.user.firstName} ${userData?.user.lastName}`;

  const handleActivateWithoutCode = () => {
    if (canNotActivateCoupons) {
      showNewUserPromotionIneligibleToast(userName);
      return;
    }
    activateCampaign({
      userGroupId: currentUserGroupId!,
      campaignId: campaign.id,
      brandDomain: campaign.brandDomain
    });
  };

  if (claimStatusError) {
    return <ApiErrorMessageWithTrans error={claimStatusError} />;
  }

  if (isClaimStatusPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (isActivateCampaignSuccess) {
    return <BenefitsActivatedCard />;
  }

  if (claimStatus?.isAlreadyJoined) {
    return <BenefitsActivatedCard variant='alreadyLinked' />;
  }

  if (requiresAuthCode) {
    return (
      <NoddiBasicCard className='flex flex-col gap-2'>
        <p className='font-semibold text-primary'>
          <Trans>Enter discount code to connect the campaign benefits to your account</Trans>
        </p>
        <form.AppForm>
          <form
            id={AUTH_CODE_FORM_ID}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className='flex flex-col gap-1'>
              {activateCampaignError && <ApiErrorMessageWithTrans error={activateCampaignError} />}
              <form.AppField name='authCode'>{(field) => <field.Input required />}</form.AppField>
              <div className='mt-2 flex justify-end'>
                <form.Subscribe selector={(state) => state.isSubmitting}>
                  {(isSubmitting) => (
                    <form.SubscribeButton
                      formId={AUTH_CODE_FORM_ID}
                      label={t`Activate your discount`}
                      disabled={isSubmitting || isActivateCampaignPending}
                    />
                  )}
                </form.Subscribe>
              </div>
            </div>
          </form>
        </form.AppForm>
      </NoddiBasicCard>
    );
  }

  return (
    <CampaignClaimBottomBar>
      <div className='flex flex-col gap-4'>
        {activateCampaignError && <ApiErrorMessageWithTrans error={activateCampaignError} />}
        <NoddiButton
          className='w-full md:w-fit md:self-end'
          loading={isActivateCampaignPending}
          onClick={handleActivateWithoutCode}
        >
          <Trans>Activate your discount</Trans>
        </NoddiButton>
      </div>
    </CampaignClaimBottomBar>
  );
};
